import type { ChartTheme } from './types';

/**
 * Built-in default color map.
 * Matches apps/care/src/utils/charts/chartConfig.ts and individual chart components.
 */
export const defaultColors: Record<string, string> = {
  // Sleep
  sleepQuality:       '#10b981', // green-500
  sleepDuration:      '#10b981', // green-500
  deepSleep:          '#1e3a8a', // blue-900
  lightSleep:         '#60a5fa', // blue-400
  remSleep:           '#8b5cf6', // violet-500
  // Vitals
  heartRate:          '#ef4444', // red-500
  heartRateRange:     'rgba(239, 68, 68, 0.15)',
  breathingRate:      '#3b82f6', // blue-500
  breathingRateRange: 'rgba(59, 130, 246, 0.15)',
  hrv:                '#8b5cf6', // violet-500
  hrvEvening:         '#ec4899', // pink-500
  // Activity
  bedExits:           '#f59e0b', // amber-500
  tossTurns:          '#06b6d4', // cyan-500
  pirActivity:        '#14b8a6', // teal-500
  movements:          '#06b6d4', // cyan-500
  // Recovery
  btri:               '#f59e0b', // amber-500
  recoveryScore:      '#10b981', // green-500
};

const builtInDefaults: ChartTheme = {
  colors: defaultColors,
  gridLineColor: '#e5e7eb',
  axisLabelColor: '#6b7280',
  tooltipBackground: 'rgba(0,0,0,0.85)',
  fontFamily: 'inherit',
};

let activeTheme: ChartTheme = { ...builtInDefaults };

/**
 * Sets the module-level active theme. Call once in each app's entry point (e.g. main.tsx).
 * Merges with built-in defaults — only specified keys are overridden.
 *
 * Note: Module-level state — not suitable for SSR or multiple concurrent app instances.
 * In unit tests running in parallel workers, call configureChartDefaults({}) in
 * beforeEach/afterEach to reset to defaults and avoid theme bleed between tests.
 */
export function configureChartDefaults(theme: ChartTheme): void {
  activeTheme = {
    ...builtInDefaults,
    ...theme,
    colors: {
      ...builtInDefaults.colors,
      ...(theme.colors ?? {}),
    },
  };
}

/**
 * Returns the current active theme. Used internally by buildChart.
 */
export function getChartDefaults(): ChartTheme {
  return activeTheme;
}
